import React, { useState, useEffect, useMemo } from "react";
import IssueAnalysis from "./Issue_Analysis";
import TeamWorkload from "./TeamWorkload";
import { Box, Grid, Typography } from "@mui/material";
import moment from "moment";
import SprintAnalysis from "./Sprint_Analysis";
import BurndownChart from "./BurndownChart";
import BreadcrumbsComponent from "../components/BreadcrumbsComponent";
import { NormalSkeleton, CircularSkeleton } from "../common/Skeleton";
import { useSelector } from "react-redux";
import SprintPicker from "./SprintPicker";
import { cardSx, INK, statusColor } from "./chartTheme";
import { ChartCard, StatTile, StatusPill } from "./ReportCards";


const Reports = () => {
  const [sprintOptions, setSprintOptions] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const { Skeleton, Projects } = useSelector((state) => state.ProjamAppSlice);
  let backlogId = Projects?.selectedProject?.selectedProjectBackLogId;
  let projectId = Projects?.selectedProject?.selectedProjectData?._id;
  let projectData = Projects?.selectedProject?.selectedProjectData || {};
  let LastFiveActiveSprint = Projects.selectedProject.lastFiveSprintsInfo || [];

  useEffect(() => {
    if (backlogId && projectId, LastFiveActiveSprint.length > 0) {
      getProjectInfo();
    }
  }, [backlogId, projectId, LastFiveActiveSprint]);

  const getProjectInfo = async () => {
    try {
      let response = LastFiveActiveSprint;
      setSprintOptions(response);

      let activeSprint = response?.find((sprint) => sprint?.active === true);
      let fallbackSprint = !activeSprint ? response?.[0] : null;
      let defaultSprint = activeSprint || fallbackSprint;

      if (defaultSprint) {
        setSelectedSprint(defaultSprint?._id);
      }
    } catch (error) {
      console.error("Error fetching sprint data:", error);
    }
  };

  /* one selection drives every panel on the page */
  const handleChange = (e) => setSelectedSprint(e.target.value);

  const sprintData = useMemo(
    () => sprintOptions.filter((eachSprint) => eachSprint?._id === selectedSprint),
    [sprintOptions, selectedSprint]
  );
  const sprint = sprintData[0];

  const summary = useMemo(() => {
    const issues = sprint?.issues || [];
    const counts = sprint?.IssuecountObj || {};
    const total = issues.length;
    /* "Done" and "To Do" are the two fixed ends of the workflow; whatever a
       project puts between them counts as work already underway */
    const done = counts?.Done || 0;
    const toDo = counts?.["To Do"] || 0;
    const start = sprint?.startDate ? moment(sprint.startDate) : null;
    const end = sprint?.endDate ? moment(sprint.endDate) : null;

    return {
      total,
      done,
      toDo,
      inProgress: Math.max(total - done - toDo, 0),
      donePct: total ? Math.round((done * 100) / total) : sprint?.completed ? 100 : 0,
      storyPoints: sprint?.initiallyplannedStoryPoints || 0,
      range: start && end ? `${start.format("DD MMM")} – ${end.format("DD MMM YYYY")}` : "",
      daysLeft: end ? Math.max(end.startOf("day").diff(moment().startOf("day"), "days"), 0) : null,
    };
  }, [sprint]);

  const sprintState = sprint?.active ? "active" : sprint?.completed ? "completed" : "neutral";
  const sprintStateLabel = sprint?.active ? "Active" : sprint?.completed ? "Completed" : "Not started";

  const sprintPicker = !Skeleton ? (
    <SprintPicker options={sprintOptions} value={selectedSprint} onChange={handleChange} />
  ) : (
    <NormalSkeleton width={300} height={58} />
  );

  return (
    <div className="reports-page-container-cls">
      <div className="ps-2">
        {!Skeleton ? <BreadcrumbsComponent /> : <NormalSkeleton width={200} extraStyles="my-3 " />}
      </div>

      <Box sx={{ px: 1, pb: 3 }}>
        {/* Top row — the project, and the one sprint filter every panel below reads */}
        <Box
          sx={{
            ...cardSx,
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "flex-start",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 2.5 },
            px: 2.5,
            py: 2.25,
            mb: 2,
          }}
        >
          {!Skeleton ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, minWidth: 0, flex: "0 1 auto" }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#eef3fc",
                  color: "#2a78d6",
                  fontSize: "15px",
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                }}
              >
                {(projectData?.key || projectData?.title || "P").slice(0, 2).toUpperCase()}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "20px !important",
                    fontWeight: "700 !important",
                    color: INK.primary,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {projectData?.title || "Project"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                  <Typography sx={{ fontSize: "12.5px !important", color: INK.muted }}>
                    Sprint analytics
                  </Typography>
                  {sprint?.name ? (
                    <>
                      <Typography sx={{ fontSize: "12.5px !important", color: INK.grid }}>|</Typography>
                      <Typography sx={{ fontSize: "12.5px !important", color: INK.secondary, fontWeight: 600 }}>
                        {sprint.name}
                      </Typography>
                    </>
                  ) : null}
                  {summary.range ? (
                    <>
                      <Typography sx={{ fontSize: "12.5px !important", color: INK.grid }}>|</Typography>
                      <Typography sx={{ fontSize: "12.5px !important", color: INK.muted }}>
                        {summary.range}
                      </Typography>
                    </>
                  ) : null}
                  {sprint ? <StatusPill label={sprintStateLabel} tone={sprintState} /> : null}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box>
              <NormalSkeleton width={240} height={28} extraStyles="my-1" />
              <NormalSkeleton width={320} extraStyles="my-1" />
            </Box>
          )}

          <Box
            sx={{
              display: { xs: "none", md: "block" },
              alignSelf: "stretch",
              width: "1px",
              backgroundColor: INK.grid,
            }}
          />
          <Box sx={{ flex: "0 0 auto", width: { xs: "100%", md: "auto" } }}>{sprintPicker}</Box>
        </Box>

        {/* Sprint headlines */}
        <Grid container spacing={2} sx={{ mb: 0 }}>
          <Grid item xs={6} md={3}>
            {!Skeleton ? (
              <StatTile
                label="Total issues"
                value={summary.total}
                hint={`${summary.toDo} not started yet`}
              />
            ) : (
              <NormalSkeleton width={"100%"} height={96} />
            )}
          </Grid>
          <Grid item xs={6} md={3}>
            {!Skeleton ? (
              <StatTile
                label="Completed"
                value={summary.done}
                hint={`${summary.donePct}% of sprint scope`}
                dotColor={statusColor("done")}
              />
            ) : (
              <NormalSkeleton width={"100%"} height={96} />
            )}
          </Grid>
          <Grid item xs={6} md={3}>
            {!Skeleton ? (
              <StatTile
                label="In progress"
                value={summary.inProgress}
                hint="moving through the workflow"
                dotColor={statusColor("in progress")}
              />
            ) : (
              <NormalSkeleton width={"100%"} height={96} />
            )}
          </Grid>
          <Grid item xs={6} md={3}>
            {!Skeleton ? (
              <StatTile
                label="Story points"
                value={summary.storyPoints}
                hint={
                  summary.daysLeft === null
                    ? "planned at sprint start"
                    : `${summary.daysLeft} ${summary.daysLeft === 1 ? "day" : "days"} left in sprint`
                }
              />
            ) : (
              <NormalSkeleton width={"100%"} height={96} />
            )}
          </Grid>
        </Grid>

        {/* Sprint analytics */}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} lg={5}>
            <ChartCard title="Issues analysis" subtitle="Issues by status in this sprint">
              {!Skeleton ? (
                <IssueAnalysis pieChartData={sprintData} />
              ) : (
                <div className="d-flex justify-content-center">
                  <CircularSkeleton width={200} height={200} />
                </div>
              )}
            </ChartCard>
          </Grid>

          <Grid item xs={12} lg={7}>
            <ChartCard title="Team workload" subtitle="Heaviest workload first">
              {!Skeleton ? (
                <TeamWorkload pieChartData={sprintData} />
              ) : (
                <NormalSkeleton width={"96%"} height={260} extraStyles="my-1" />
              )}
            </ChartCard>
          </Grid>

          {/* 60 / 40 — the burndown carries a date per day, so it takes the
              wider side; the five-sprint bar chart reads fine narrow */}
          <Grid item xs={12} lg={12} sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 2 }}>
            <Box sx={{ flex: { lg: "1 1 60%" }, minWidth: 0, display: "flex", "& > *": { width: "100%" } }}>
            <ChartCard
              title="Burndown chart"
              subtitle={
                summary.storyPoints
                  ? `Remaining work against the ideal line · ${summary.storyPoints} story points planned`
                  : "Remaining work against the ideal line"
              }
              bodySx={{ px: 1.5 }}
            >
              {!Skeleton ? (
                <BurndownChart burnDownChartData={sprintData} />
              ) : (
                <NormalSkeleton width={"96%"} height={200} extraStyles="my-1" />
              )}
            </ChartCard>
            </Box>

            <Box sx={{ flex: { lg: "1 1 40%" }, minWidth: 0, display: "flex", "& > *": { width: "100%" } }}>
            {/* cross-sprint, so it deliberately ignores the sprint filter above */}
            <ChartCard title="Sprint analysis" subtitle="Duration across the last five sprints">
              {!Skeleton ? (
                <SprintAnalysis />
              ) : (
                <NormalSkeleton width={"90%"} height={220} extraStyles="my-1" />
              )}
            </ChartCard>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default Reports;
